import envs from "@/envs";
import { setJWT, getJWT } from "@/utils/token";
import axios from "axios";
import day from "dayjs";
import _ from "lodash";
import Vue from "vue";

import { useUserStore } from "@/store/userStore";
import { useRouter } from "vue-router";

const baseUrl = envs.buildUrl;
let loadingQueue = []; // global loading에서 사용하기 위한 다중 호출 제어 Queue
const loadingTerm = 500; // API 비동기로 인해 시점이 안맞아서 깜빡거리는 현상을 제어하기 위한 텀 기간 부여 용

// MFE를 위한 host 데이터 참조
let inited = false;
const hostRouter = useRouter();

let userStore;

export function init() {
  const vm = window._vue;

  userStore = useUserStore();

  if (vm) {
    inited = true;

    return;
  }

  inited = true;
}

// 기본 meta 값
function _getDefaultMeta() {
  return {
    useTokenUpdate: false, // 받아온 response에서 token 값을 가져와 재 셋팅을 할 경우 true로 셋팅한다.
    useAuth: true, // 인증 모듈을 사용한다. false가 되었을 경우 401 상태에서도 Login 화면으로 가지 않는다. (i18n과 같은 특별한 경우에서만 사용)
    apiVersion: envs.apiVersion, // API 버전을 호출 API 별로 정의 한다. 기본 값은 envs에 정의한 값으로 셋팅된다.
    useErrorMessage: true, // response 시, 에러가 날 경우 에러 메시지를 출력하는 여부를 셋팅한다. 기본은 true
    useResponseAll: false, // request 실행 이후, API로 돌아오는 Obj를 Axios response 전문으로 변경되어 전달한다. 기본은 false
    useProgress: true, // API 사용 간 Global Lock Progressive Bar 를 사용하는 것을 의미.
    useErrorConfirm: true, // API 사용 간 에러가 발생할 경우, Error 전문 Confirm box가 출력되는 여부
  };
}

// loading progressivebar cloas 처리 및 API 실행 시간을 response 하는 로직
function checkLoadingProgress(meta) {
  const now = +new Date();
  let target = _.find(loadingQueue, { key: meta.logkey });
  if (!target) {
    // 없는 경우, 방어 로직
    target = {
      time: now,
    };
  }
  const loadingTime = now - target.time;

  // API 비동기 딜레이 부여 (깜빡거림 방지)
  setTimeout(() => {
    target.done = true;

    // 모든 비동기 API들이 다 수행됬는지 확인하는 로직
    if (loadingQueue.every((q) => q.done)) {
      loadingQueue = [];

      window.$loadingDone();
    }
  }, loadingTerm);

  return loadingTime;
}

// timeout 과 같은 에러 발생 시, 로딩 부분을 초기화
function initLoadingProgress() {
  loadingQueue = [];

  window.$loadingDone();
}

const service = axios.create({
  baseURL: baseUrl,
  timeout: 20000, // timeout은 20초로 설정
});

// axios의 reqeust 인터셉터 진행 부
service.interceptors.request.use(
  (config) => {
    // console.log("config", config);

    if (!inited) init();

    const configSetting = config;
    const { systemId } = envs;
    const logKey = day().format(
      `YYYYMMDDHHmmssSSS${systemId}${_.random(1000, 9999)}`
    );

    // meta 값 기본 정의
    if (!configSetting.meta) configSetting.meta = {};
    configSetting.meta = Object.assign(_getDefaultMeta(), config.meta);
    // console.log("configSetting.meta", configSetting.meta);
    configSetting.meta.logkey = logKey;

    configSetting.headers["X-APIVERSION"] = config.meta.apiVersion;
    configSetting.headers["X-LOGKEY"] = logKey;
    configSetting.headers["X-APP"] = envs.systemId;
    configSetting.headers["Access-Control-Allow-Origin"] = "*";

    //const jwt = getJWT();
    const jwt = userStore.token;
    // console.log("jwt", jwt);
    configSetting.headers["Authorization"] = jwt;

    // Loading 처리
    if (config.useProgress !== false) {
      window.$loading();
    }

    // API 고유 값과, 완료 상태 값 셋팅
    loadingQueue.push({
      key: logKey,
      done: false,
      time: +new Date(),
    });

    return configSetting;
  },
  (error) => {
    // 에러가 날경우 진행 부분
    // console.log('REQUEST error', error) // for debug
    Promise.reject(error);

    window.$loadingDone();
  }
);

// axios의 response 인터셉터
service.interceptors.response.use(
  async (response) => {
    // console.log("request.js response", response);

    const { meta } = response.config;

    // Blob 또는 ArrayBuffer 응답인 경우 바로 반환 처리
    if (
      response.config.responseType === "blob" ||
      response.config.responseType === "arraybuffer" ||
      response.data instanceof Blob ||
      response.data instanceof ArrayBuffer
    ) {
      checkLoadingProgress(meta);

      if (response.headers) {
        setJWT(response.headers.authorization);
      }

      // 서버에서 에러 등으로 JSON 형식의 Blob을 반환한 경우 파싱 처리
      if (response.data instanceof Blob) {
        const type = response.data.type || "";
        if (
          type.includes("application/json") ||
          type.includes("text/") ||
          response.data.size < 4096
        ) {
          try {
            const text = await response.data.text();
            const trimmed = text.trim();
            if (trimmed.startsWith("{") && trimmed.endsWith("}")) {
              const json = JSON.parse(trimmed);
              if (
                json &&
                (json.Message !== undefined ||
                  json.Code !== undefined ||
                  json._Result !== undefined)
              ) {
                if (json.Code === 400) {
                  userStore.clearSession();
                }
                return json;
              }
            }
          } catch (e) {
            // JSON 파싱 실패 시 원본 Blob 반환
          }
        }
      }

      return response.data;
    }

    let res = {};

    // console.log('[UI Framework api res]', response)
    // axios를 통해 response 내역 중 data에 서버에서 response된 값이 들어온다.
    // 각각 중복 랩핑 된 내역을 처리한다.
    if (response.data && response.data.datas) {
      res = response.data.datas;
    } else if (response.data && response.data.data) {
      res = response.data.data;
    } else if (response.data && response.data.dataList) {
      res = response.data.dataList;
    } else {
      res = response.data;
    }
    // console.log("request.js res", res);

    if (res.Code === 400) {
      // 로그인 세션정보가 없습니다. 다시 로그인하십시오.
      userStore.clearSession();
    }

    // header 저장
    if (response.headers) {
      res.headers = response.headers;
      setJWT(response.headers.authorization);
    }

    // res 규격이 Object가 아닐 경우 Object로 셋팅
    if (!Array.isArray(res) && typeof res !== "object") {
      res = {
        body: res,
      };
    }

    // loading 및 시간 처리
    const execTime = checkLoadingProgress(meta);
    if (response.data && response.data.result) {
      if (typeof response.data.result === "object") {
        res.result = response.data.result;
      } else res.result = {};
    } else {
      res.result = {};
    }

    // API 수행 시간 적용
    res.result.execTime = execTime;
    res.result.resMsg = "";
    if (res.result.total) {
      const tmpl = _.template(
        `${res.result.total}건 중, ${res.result.success}건 성공, ${res.result.fail}건 실패. ${res.result.execTime}ms`
      );
      const t = res.result.total;
      res.result.resMsg = tmpl({
        total: t.count,
        success: t.success,
        fail: t.fail,
        execTime,
      });
    }

    return res;
  },
  (error) => {
    console.log("msg >>>", error.message); // for debug

    // response 부분 객체 내 meta 사용을 위한 기본 값 셋팅
    let meta = {};

    // request 기본 에러 값은 499로 설정한다.
    // let status = '499'
    // let msg = error.message

    if (error.response && error.response.config) {
      // request에서 meta 값이 있을 경우
      meta = error.response.config.meta;
      checkLoadingProgress(meta);
    } else {
      meta = _getDefaultMeta();
      initLoadingProgress();
    }

    if (error.response) {
      // result에 메시지가 있을 경우 메시징 추가 처리
      if (
        error.response.data &&
        error.response.data.result &&
        error.response.data.result.desc
      ) {
        // msg = error.response.data.result.desc
      }

      // error.response가 없을 경우도 존재 - Network 문제
      if (meta.useAuth && error.response.status === 401) {
        // 401 에러
        // msg = '세션이 만료되었거나, 로그인이 실패되었습니다.'
        // 남아 있는 세션 제거
        // hostStore.dispatch('ClearSession')

        // login 페이지로 이동
        hostRouter.replace("/auth/login");
      } else if (meta.useAuth && error.response.status === 403) {
        // login 페이지로 이동
        // hostRouter.push('/forbidden')
        hostRouter.replace("/auth/login");
      }

      // status = error.response.status
    }

    // 에러 출력이 가용할 경우
    if (meta.useErrorMessage) {
      // 에러 경우 공통 에러 Confirm 창을 출력시킨다.
      // hostStore.dispatch('SetErrorMsg', { msg, status })
      // hostStore.dispatch('ShowErrorConfirm')
    }

    return Promise.reject(error);
  }
);

export default service;
